import { useCallback, useEffect, useState } from "react";
import type { AssetView, SeedanceGenerationHistoryResponse } from "@seedance/contracts";
import { apiClient, type ApiClient } from "../../api/client.js";

type HistoryState =
  | { status: "loading" }
  | { status: "ready"; data: SeedanceGenerationHistoryResponse }
  | { status: "error" };

const stateLabel: Record<string, string> = {
  queued: "排队中",
  leased: "已分配",
  submitting: "正在提交",
  submitted: "已提交",
  running: "生成中",
  submit_uncertain: "等待管理员核对",
  succeeded: "已完成",
  failed: "失败",
  cancelled: "已取消"
};

function formatTokens(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/gu, ",");
}

export interface GenerationAssetHistoryDialogProps {
  open: boolean;
  onClose(): void;
  onAddAsset(asset: AssetView): void;
  client?: ApiClient;
}

/**
 * The canvas keeps generation output within the current work context. The
 * full records page remains available from the header for account-level
 * accounting, while this dialog is deliberately only an asset picker.
 */
export function GenerationAssetHistoryDialog({
  open,
  onClose,
  onAddAsset,
  client = apiClient
}: GenerationAssetHistoryDialogProps) {
  const [state, setState] = useState<HistoryState>({ status: "loading" });
  const [addingAssetId, setAddingAssetId] = useState<string>();

  const load = useCallback(async () => {
    if (!open) return;
    setState({ status: "loading" });
    try {
      setState({ status: "ready", data: await client.listMyGenerationHistory({ limit: 50 }) });
    } catch {
      setState({ status: "error" });
    }
  }, [client, open]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose, open]);

  if (!open) return null;
  const data = state.status === "ready" ? state.data : undefined;
  const outputItems = data?.items.flatMap((item) => item.outputAssetIds.map((assetId) => ({
    generationId: item.id,
    assetId,
    model: typeof item.snapshot.request === "object" && item.snapshot.request !== null && "model" in item.snapshot.request
      ? String(item.snapshot.request.model)
      : "Seedance",
    state: item.state
  }))) ?? [];
  const add = async (assetId: string) => {
    if (addingAssetId) return;
    setAddingAssetId(assetId);
    try {
      onAddAsset(await client.getAsset(assetId));
      onClose();
    } finally {
      setAddingAssetId(undefined);
    }
  };

  return (
    <div className="studio-generation-history-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="studio-generation-history-dialog" role="dialog" aria-modal="true" aria-label="历史素材">
        <header>
          <div>
            <h2>历史素材</h2>
            <p>仅显示当前账号的 Seedance 生成结果；需要时可重新加入当前画布。</p>
          </div>
          <button type="button" aria-label="关闭历史素材" onClick={onClose}>×</button>
        </header>
        {data ? (
          <div className="studio-generation-history-summary" aria-label="生成用量摘要">
            <span>累计 Token：{formatTokens(data.summary.totalTokens)}</span>
            <span>视频历史：{outputItems.length}</span>
          </div>
        ) : null}
        {state.status === "loading" ? <p className="studio-generation-history-empty">正在加载历史素材…</p> : null}
        {state.status === "error" ? (
          <p className="studio-generation-history-empty" role="alert">
            历史素材加载失败。
            <button type="button" onClick={() => void load()}>重试</button>
          </p>
        ) : null}
        {state.status === "ready" && outputItems.length === 0 ? (
          <p className="studio-generation-history-empty">尚无 Seedance 生成素材</p>
        ) : null}
        {state.status === "ready" && outputItems.length > 0 ? (
          <ul className="studio-generation-history-grid">
            {outputItems.map((item) => (
              <li key={`${item.generationId}:${item.assetId}`}>
                <video controls preload="metadata" src={`/api/assets/${encodeURIComponent(item.assetId)}/content`} />
                <strong>{item.model}</strong>
                <small>{stateLabel[item.state] ?? item.state}</small>
                <button type="button" disabled={addingAssetId === item.assetId} onClick={() => void add(item.assetId)}>
                  {addingAssetId === item.assetId ? "正在添加…" : "添加到画布"}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
